import * as React from 'react'
import { useMemo, useCallback, useEffect, useRef, useState } from 'react'
import { styled } from 'styled-components'
import { Row, Col, Accordion } from 'react-bootstrap'
import { pipe, RA, RD, O, t, T } from '@packages/prelude'
import * as domain from '@packages/domain'
import { AccordionEventKey } from 'react-bootstrap/esm/AccordionContext'
import { Step } from './Step'
import { OptionsGrid } from './OptionsGrid'
import { PriceDisplay } from './PriceDisplay'
import { Purchase } from './Purchase'
import { Navigation } from './Navigation'
import * as select from '~/selectors'
import { useConfigurationState, useNavigationActions } from '~/hooks'
import { isOptionSelected, getStepSelectedOptions } from '~/utils/configurationHelpers'
import { Configuration } from '~/store'
import { ConfigurationHelpers } from '~/utils'

const Container = styled.div`
  width: 100%;
  margin: 0 auto;
`

const StickyContainer = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;

  background: var(--bs-light);
  border-top: 1px solid #e0e0e0;

  padding: 1rem 0;

  &::before {
    content: '';
    height: 100%;

    position: absolute;
    z-index: -1;
    top: 0;

    left: -2.25rem;
    right: -2.25rem;
    @media (max-width: 767.98px) {
      left: -1.5rem;
      right: -1.5rem;
    }

    background: var(--bs-secondary);
    box-shadow: 0 0 0.6rem 0.15rem rgba(52, 52, 52, 0.5); /* $body-color */
  }
`

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  font-size: 18px;
  color: #666;
`

const ErrorMessage = styled.div`
  color: #e74c3c;
  padding: 20px;
  border: 1px solid #e74c3c;
  border-radius: 4px;
  margin-bottom: 20px;
`

export interface ConfiguratorAppProps {
  configuraton: RD.RemoteData<Error, domain.Configurator.Type>
  basePrice: number
  productId: number
  categoryName: t.NonEmptyString
  deliveryNotice: t.NonEmptyString
}

// Main component
export const ConfiguratorApp: React.FC<ConfiguratorAppProps> = ({
  configuraton,
  basePrice,
  productId,
  categoryName,
  deliveryNotice
}) => {
  return (
    <Container>
      {pipe(
        configuraton,
        RD.fold(
          () => <LoadingIndicator>Loading configurator...</LoadingIndicator>,
          () => <ErrorMessage>Error loading configurator data</ErrorMessage>,
          () => <LoadingIndicator>Loading configurator...</LoadingIndicator>,
          data => (
            <ConfiguratorContent
              data={data}
              basePrice={basePrice}
              productId={productId}
              categoryName={categoryName}
              deliveryNotice={deliveryNotice}
            />
          )
        )
      )}
    </Container>
  )
}

interface ConfiguratorContentProps {
  data: domain.Configurator.Type
  basePrice: number
  productId: number
  categoryName: t.NonEmptyString
  deliveryNotice: t.NonEmptyString
}

const ConfiguratorContent: React.FC<ConfiguratorContentProps> = ({
  data,
  basePrice,
  productId,
  categoryName,
  deliveryNotice
}) => {
  const [state, dispatch] = useConfigurationState(data)
  const [manuallyCollapsedSteps, setManuallyCollapsedSteps] = useState<ReadonlyArray<domain.StepId.Type>>([])
  const purchaseRef = useRef<HTMLDivElement>(null)
  const hasAutoClosedRef = useRef(false)

  // Derived state using new simplified selectors
  const editableSteps = useMemo(() => select.Configuration.editableSteps(state), [state])
  const previewSteps = useMemo(() => select.Configuration.previewSteps(state), [state])
  const canNavigateNext = useMemo(() => select.Configuration.canNavigateNext(state), [state])
  const canNavigatePrevious = useMemo(() => select.Configuration.canNavigatePrevious(state), [state])
  const progressCurrent = useMemo(() => select.Configuration.getProgress(state), [state])
  const progressTotal = useMemo(() => select.Configuration.getProgressTotal(state), [state])
  const totalPrice = useMemo(() => select.Configuration.totalPrice(state, basePrice), [state, basePrice])

  const isConfigurationComplete = useMemo(() => select.Configuration.isConfigurationComplete(state), [state])

  // Navigation actions with built-in side effects
  const { navigateNext, navigatePrevious } = useNavigationActions(state, dispatch)

  const handleOptionSelect = useCallback(
    (option: domain.Option.Option) => {
      dispatch(Configuration.optionToggled(option))
    },
    [dispatch]
  )
  const handleOptionNoneSelect = useCallback(
    (option: domain.Option.OptionNone) => {
      dispatch(Configuration.optionNoneSelected(option))
    },
    [dispatch]
  )

  const onStepSelect = useCallback(
    (eventKeys: AccordionEventKey) => {
      pipe(
        eventKeys,
        t.readonlyArray(t.NonEmptyString).decode,
        O.fromEither,
        O.map(RA.map(domain.StepId.Iso.wrap)),
        O.map(stepIds => {
          setManuallyCollapsedSteps(
            pipe(
              editableSteps,
              RA.map(step => step.id),
              RA.difference(domain.StepId.Eq)(stepIds)
            )
          )
        })
      )
    },
    [editableSteps]
  )

  // Determine active accordion keys: editable steps minus manually collapsed ones
  const activeAccordionKeys = useMemo(() => {
    return editableSteps.map(step => step.id).filter(stepId => !manuallyCollapsedSteps.includes(stepId))
  }, [editableSteps, manuallyCollapsedSteps])

  // Auto-close all steps only once when first completing
  // Scroll to Purchase button when configuration is complete
  useEffect(() => {
    if (isConfigurationComplete && purchaseRef.current && !hasAutoClosedRef.current) {
      pipe(
        T.fromIO(() => (window.location.hash = '#purchase')),
        T.chain(() =>
          T.fromIO(
            () =>
              new Promise<void>(resolve => {
                // Use scrollend where supported, setTimeout fallback for Safari
                if ('onscrollend' in window) {
                  window.addEventListener('scrollend', () => resolve(), { once: true })
                } else {
                  setTimeout(resolve, 300) // Reasonable delay for Safari
                }
              })
          )
        ),
        T.map(() => {
          setManuallyCollapsedSteps(editableSteps.map(step => step.id))
          hasAutoClosedRef.current = true
        }),
        T.execute
      )
    }
  }, [isConfigurationComplete, editableSteps])

  const isActiveStep = useCallback((step: domain.Step.Type) => activeAccordionKeys.includes(step.id), [activeAccordionKeys])

  return (
    <>
      <Row className="mb-5">
        <Col>
          <Accordion alwaysOpen={true} activeKey={[...pipe(activeAccordionKeys, RA.map(String))]} onSelect={onStepSelect}>
            {/* Render editable steps */}
            {pipe(
              editableSteps,
              RA.map(step => {
                const stepSelectedOptions = getStepSelectedOptions(state, step.id)

                return (
                  <Step
                    key={String(step.id)}
                    eventKey={String(step.id)}
                    step={step}
                    isActive={isActiveStep(step)}
                    selectedOptions={stepSelectedOptions}>
                    <OptionsGrid
                      step={step}
                      isOptionSelected={optionId => isOptionSelected(state, step.id, optionId)}
                      onOptionSelect={handleOptionSelect}
                      onOptionNoneSelect={handleOptionNoneSelect}
                    />
                  </Step>
                )
              })
            )}

            {/* Render preview steps */}
            {pipe(
              previewSteps,
              RA.map(step => <Step.Preview key={String(step.id)} eventKey={String(step.id)} step={step} />)
            )}
          </Accordion>
        </Col>
      </Row>

      <StickyContainer>
        <Row>
          <Col>
            <Navigation
              canNavigateNext={canNavigateNext}
              canNavigatePrevious={canNavigatePrevious}
              onNavigateNext={navigateNext}
              onNavigatePrevious={navigatePrevious}
              progressCurrent={progressCurrent}
              progressTotal={progressTotal}
            />
          </Col>
          <Col xs="auto">
            <PriceDisplay totalPrice={totalPrice} />
          </Col>
        </Row>
      </StickyContainer>

      {isConfigurationComplete && (
        <Purchase
          ref={purchaseRef}
          id="purchase"
          selectedOptions={ConfigurationHelpers.pruneConfigurationState(state).selectedOptions}
          configuration={data}
          productId={productId}
          categoryName={categoryName}
          deliveryNotice={deliveryNotice}
          price={totalPrice}
        />
      )}
    </>
  )
}
